/**
 * EINE gemeinsame Quelle für Angebots-Summen (Client UI, DOCX, Server-PDF, Persistenz).
 *
 * Vorher rechnete jede Oberfläche die Zeilen-Netto-Summe selbst — mit subtilen
 * Abweichungen (docx nahm roh preis*menge ohne Rabatt) und OHNE das Konzept einer
 * Bedarfs-/Alternativposition, die NICHT in die Hauptsumme (Auftragssumme) zählt. Genau
 * dieses Konzept braucht F1. Damit keine Bestandssumme still umgerechnet wird, ist die
 * Zeilen-Mathematik bit-genau die bisherige (getLineNetAfterDiscount / getItemNetAfterLineDiscount).
 */

// Bewusst KEIN Import von QuoteItem-Typ: die Funktionen sollen auch mit GAEBEntry/Position
// und Server-Rohobjekten arbeiten. Wir lesen defensiv über bekannte Feldnamen.
type LineLike = {
  preis?: number | string;
  /** true = "kein Preis erfasst" (Import ohne Preise) — unterscheidet fehlenden
   * Preis von echten 0,00-€-Positionen. preis ist dann numerisch 0. */
  preisFehlt?: boolean;
  menge?: number | string;
  rabattTyp?: 'percent' | 'absolute';
  rabattWert?: number | string;
  isBedarfsposition?: boolean;
  isAlternativposition?: boolean;
  isHeader?: boolean;
};

export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function toSafeNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(',', '.').trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

/** Zeilen-Netto vor Rabatt: max(0,preis) * max(0,menge), gerundet. */
export function getLineBaseNet(item: LineLike): number {
  const price = toSafeNumber(item?.preis, 0);
  const quantity = toSafeNumber(item?.menge, 0);
  return roundMoney(Math.max(0, price) * Math.max(0, quantity));
}

/** Zeilen-Rabattbetrag (absolut gedeckelt auf Basis, Prozent gedeckelt auf 100). */
export function getLineDiscountAmount(item: LineLike): number {
  const baseNet = getLineBaseNet(item);
  if (baseNet <= 0) return 0;

  const discountValue = Math.max(0, toSafeNumber(item?.rabattWert, 0));
  if (discountValue <= 0) return 0;

  const discountType = item?.rabattTyp === 'absolute' ? 'absolute' : 'percent';
  if (discountType === 'absolute') {
    return roundMoney(Math.min(discountValue, baseNet));
  }
  const normalizedPercent = Math.min(100, discountValue);
  return roundMoney((baseNet * normalizedPercent) / 100);
}

/** Kanonisches Zeilen-Netto NACH Zeilenrabatt. Identisch zur bisherigen Berechnung. */
export function getLineNet(item: LineLike): number {
  const baseNet = getLineBaseNet(item);
  const discount = getLineDiscountAmount(item);
  return roundMoney(Math.max(0, baseNet - discount));
}

// --- Positionsart: was zählt in die Hauptsumme, was nur nachrichtlich? ---

export function isBedarfsposition(item: LineLike): boolean {
  return Boolean(item?.isBedarfsposition);
}

export function isAlternativposition(item: LineLike): boolean {
  return Boolean(item?.isAlternativposition);
}

/** Bedarfs-/Eventual- UND Alternativpositionen bleiben aus der Auftrags-Hauptsumme
 * heraus (GAEB-Standard: Bedarfsposition = Provis; Alternativ = Nebenangebot). */
export function isExcludedFromMainSum(item: LineLike): boolean {
  return isBedarfsposition(item) || isAlternativposition(item);
}

// --- "Kein Preis erfasst" (preisFehlt) — EINE Quelle für alle Ausgabewege ---

/** true, wenn die Zeile als "Preis fehlt" markiert ist (Import ohne Preise).
 * Ausgabewege drucken dann "–" statt 0,00 € und weisen unvollständige Summen aus. */
export function hasMissingPrice(item: LineLike): boolean {
  return item?.preisFehlt === true;
}

/** Anzahl Leistungszeilen ohne erfassten Preis (Gliederungszeilen zählen nicht).
 * Für den Hinweis "N Position(en) ohne Preis" unter Summenblöcken. */
export function countMissingPrices(items: ReadonlyArray<LineLike>): number {
  let count = 0;
  for (const item of items || []) {
    if (item?.isHeader === true) continue;
    if (hasMissingPrice(item)) count += 1;
  }
  return count;
}

export interface QuoteTotals {
  /** Auftragssumme netto: nur normale Positionen. */
  netMain: number;
  /** Σ Bedarfs-/Eventualpositionen (netto, nachrichtlich). */
  netBedarf: number;
  /** Σ Alternativpositionen (netto, nachrichtlich). */
  netAlternativ: number;
  /** netBedarf + netAlternativ. */
  netExcluded: number;
  /** Σ ALLER Zeilen (netMain + netExcluded) — für Kontrollzwecke. */
  netAll: number;
}

/** Zerlegt die Positionen in Haupt-/Bedarfs-/Alternativsumme. */
export function computeQuoteTotals(items: ReadonlyArray<LineLike>): QuoteTotals {
  let netMain = 0;
  let netBedarf = 0;
  let netAlternativ = 0;

  for (const item of items || []) {
    const net = getLineNet(item);
    if (isBedarfsposition(item)) {
      netBedarf += net;
    } else if (isAlternativposition(item)) {
      netAlternativ += net;
    } else {
      netMain += net;
    }
  }

  netMain = roundMoney(netMain);
  netBedarf = roundMoney(netBedarf);
  netAlternativ = roundMoney(netAlternativ);
  const netExcluded = roundMoney(netBedarf + netAlternativ);
  return {
    netMain,
    netBedarf,
    netAlternativ,
    netExcluded,
    netAll: roundMoney(netMain + netExcluded),
  };
}
